import Vue from 'vue';

// Tiempo que se reutiliza la lista de pendientes antes de volver a consultar al backend
const CACHE_MS = 30000;
// Si el backend no responde en este tiempo, se considera una falla y no se bloquea la navegación
const TIMEOUT_MS = 8000;

const state = {
  pendientes: [],
  consultadoEn: 0,           // marca de tiempo de la última consulta exitosa (0 = sin caché)
  bloqueoSuspendido: false,  // true si la encuesta obligatoria no se pudo cargar: no se atrapa al estudiante
};

const actions = {
  // Encuestas de satisfacción que le aplican al estudiante y aún no ha respondido.
  // El backend ya filtra por nivel, sede, progreso mínimo, etc.: todo lo que llega aplica.
  // Es una consulta silenciosa (sin loader ni errores). Si falla, resuelve [] y no deja caché,
  // así la siguiente navegación reintenta y el estudiante nunca queda bloqueado por una falla.
  fetchPendientes: ({commit, state}, force = false) => {
    if (!force && state.consultadoEn && Date.now() - state.consultadoEn < CACHE_MS) {
      return Promise.resolve(state.pendientes);
    }
    return new Promise((resolve) => {
      Vue.http.get('encuestas-satisfaccion/pendientes', { timeout: TIMEOUT_MS })
        .then(response => {
          commit('setPendientes', response.data);
          resolve(state.pendientes);
        })
        .catch(() => {
          commit('limpiarPendientes');
          resolve([]);
        })
    })
  },

  // Encuesta obligatoria pendiente (la primera), o null si no hay o el bloqueo está suspendido
  fetchObligatoria: ({state, dispatch}) => {
    if (state.bloqueoSuspendido) return Promise.resolve(null);
    return dispatch('fetchPendientes').then(lista => lista.find(e => e.obligatoria) || null);
  },

  // Encuesta con sus preguntas activas y opciones, lista para responder
  fetchEncuesta: ({commit}, id) => {
    commit('startProcessing', null, { root: true });
    return new Promise((resolve, reject) => {
      Vue.http.get(`encuestas-satisfaccion/${id}`)
        .then(response => {
          resolve(response.data);
        })
        .catch(error => {
          commit('setError', error, { root: true });
          reject(error);
        })
        .finally(() => {
          commit('stopProcessing', null, { root: true });
        })
    })
  },

  // respuestas: [{ pregunta_id, valor }]
  responder: ({commit}, { id, respuestas }) => {
    commit('startProcessing', null, { root: true });
    return new Promise((resolve, reject) => {
      Vue.http.post(`encuestas-satisfaccion/${id}/responder`, { respuestas })
        .then(response => {
          commit('quitarPendiente', id);
          commit('invalidar');
          resolve(response.data);
        })
        .catch(error => {
          commit('setError', error, { root: true });
          reject(error);
        })
        .finally(() => {
          commit('stopProcessing', null, { root: true });
        })
    })
  },
};

const getters = {
  // Primera encuesta pendiente (las obligatorias vienen primero), o null si no hay
  encuestaPendiente: (state) => state.pendientes.length > 0 ? state.pendientes[0] : null,
  // Hay una encuesta obligatoria que bloquea la navegación del estudiante
  navegacionBloqueada: (state) => !state.bloqueoSuspendido && state.pendientes.some(e => e.obligatoria),
};

const mutations = {
  setPendientes: (state, pendientes) => {
    state.pendientes = Array.isArray(pendientes) ? pendientes : [];
    state.consultadoEn = Date.now();
  },
  // Falla de red: sin lista ni caché, la próxima navegación vuelve a consultar
  limpiarPendientes: (state) => {
    state.pendientes = [];
    state.consultadoEn = 0;
  },
  quitarPendiente: (state, id) => {
    state.pendientes = state.pendientes.filter(e => String(e.id) !== String(id));
  },
  invalidar: (state) => {
    state.consultadoEn = 0;
  },
  suspenderBloqueo: (state, valor) => {
    state.bloqueoSuspendido = valor;
  },
  // Al cerrar sesión se limpia todo
  reset: (state) => {
    state.pendientes = [];
    state.consultadoEn = 0;
    state.bloqueoSuspendido = false;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
